import { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Seo } from '@/components/Seo';
import { SkipLink } from '@/components/SkipLink';
import { useNow } from '@/lib/useNow';
import { isSignedIn } from '@/lib/prefs';

import { homeVals } from '@/lib/vals/home';
import { platformVals } from '@/lib/vals/platform';
import { engineVals } from '@/lib/vals/engine';
import { dataVals } from '@/lib/vals/data';
import { recordVals } from '@/lib/vals/record';
import { designVals } from '@/lib/vals/design';
import { accessVals } from '@/lib/vals/access';
import { useFaqVals } from '@/lib/vals/faq';
import { useLoginVals } from '@/lib/vals/login';
import { useTerminalVals } from '@/lib/vals/terminal';

import { SiteLayout } from '@/components/SiteLayout';
import { HomePage } from '@/routes/HomePage';
import { PlatformPage } from '@/routes/PlatformPage';
import { EnginePage } from '@/routes/EnginePage';
import { DataPage } from '@/routes/DataPage';
import { RecordPage } from '@/routes/RecordPage';
import { DesignPage } from '@/routes/DesignPage';
import { AccessPage } from '@/routes/AccessPage';
import { FaqPage } from '@/routes/FaqPage';
import { LoginRoute } from '@/routes/LoginRoute.generated';
import { TerminalRoute } from '@/routes/TerminalRoute.generated';
import { NotFoundRoute } from '@/routes/NotFoundRoute';

/** Marketing pages show HH:MM, so a 30s tick is enough. */
const SLOW_TICK = 30_000;
/** The terminal status bar shows seconds. */
const FAST_TICK = 1_000;

// These derive from the static registry alone, so they are computed once rather
// than rebuilt on every render.
const PLATFORM = platformVals();
const ENGINE = engineVals();
const DATA = dataVals();
const RECORD = recordVals();
const DESIGN = designVals();
const ACCESS = accessVals();

function Home() {
  const now = useNow(SLOW_TICK);
  const v = useMemo(() => homeVals(now), [now]);
  return (
    <SiteLayout>
      <HomePage v={v} />
    </SiteLayout>
  );
}

function Login() {
  const now = useNow(SLOW_TICK);
  return <LoginRoute v={useLoginVals(now)} />;
}

/* The accordion owns state, so it needs a component boundary of its own —
   otherwise expanding an entry would re-render every route. */
function Faq() {
  return (
    <SiteLayout>
      <FaqPage v={useFaqVals()} />
    </SiteLayout>
  );
}

function Terminal() {
  const now = useNow(FAST_TICK);
  const v = useTerminalVals(now);
  return <TerminalRoute v={v} />;
}

/**
 * The demo terminal is gated in the browser only — there is no auth endpoint.
 * Sending an unsigned visitor to the sign-in screen keeps the gate honest
 * instead of leaving a link that half-works.
 *
 * The check runs after mount rather than during render: sessionStorage does not
 * exist while prerendering, and redirecting on a StaticRouter's first render is
 * a no-op that React Router warns about.
 */
function RequireDemoSession({ children }: { children: React.ReactElement }) {
  const [status, setStatus] = useState<'checking' | 'allowed' | 'denied'>('checking');

  useEffect(() => {
    setStatus(isSignedIn() ? 'allowed' : 'denied');
  }, []);

  if (status === 'checking') return null;
  if (status === 'denied') return <Navigate to="/login" replace />;
  return children;
}

/** The artifact reset scroll on every route change; routers do not by default. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <>
      <Seo />
      <ScrollToTop />
      <SkipLink />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route
          path="/platform"
          element={
            <SiteLayout>
              <PlatformPage v={PLATFORM} />
            </SiteLayout>
          }
        />
        <Route
          path="/engine"
          element={
            <SiteLayout>
              <EnginePage v={ENGINE} />
            </SiteLayout>
          }
        />
        <Route
          path="/data"
          element={
            <SiteLayout>
              <DataPage v={DATA} />
            </SiteLayout>
          }
        />
        <Route
          path="/record"
          element={
            <SiteLayout>
              <RecordPage v={RECORD} />
            </SiteLayout>
          }
        />
        <Route
          path="/design"
          element={
            <SiteLayout>
              <DesignPage v={DESIGN} />
            </SiteLayout>
          }
        />
        <Route
          path="/access"
          element={
            <SiteLayout>
              <AccessPage v={ACCESS} />
            </SiteLayout>
          }
        />
        <Route path="/faq" element={<Faq />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/terminal"
          element={
            <RequireDemoSession>
              <Terminal />
            </RequireDemoSession>
          }
        />
        <Route path="*" element={<NotFoundRoute />} />
      </Routes>
    </>
  );
}
